import { Link } from "react-router-dom";
import { Linkedin, Facebook, Mail } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import LSCMonogram from "@/components/LSCMonogram";
import MonogramSeal from "@/components/MonogramSeal";
import { OFFICES, FIRM_PHONE, PEAK_PHONE, MAIN_OFFICE_PHONE, FIRM_EMAIL, FIRM_DISCIPLINES } from "@/lib/content";

export default function Footer() {
  return (
    <footer className="panel-navy grain grain-dark mt-32" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <BrandMark variant="light" size="lg" showUrl={true} data-testid="footer-logo" />
            <div className="mt-4 text-[10px] uppercase tracking-[0.42em] text-brass font-medium" data-testid="footer-disciplines">
              Attorney <span className="text-brass/40 mx-1">·</span> CPA <span className="text-brass/40 mx-1">·</span> Business Advisor
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-ivory/70 font-medium" data-testid="footer-service-lines">
              Business
              <span className="text-brass/40 mx-2">•</span>
              Estate Planning
              <span className="text-brass/40 mx-2">•</span>
              Probate
              <span className="text-brass/40 mx-2">•</span>
              Trusts
              <span className="text-brass/40 mx-2">•</span>
              Succession
            </div>
            <h3 className="font-display text-4xl md:text-5xl mt-10 leading-[1.05]">
              Attorney. CPA.<br/>
              <span className="italic font-cormorant text-brass">Business Advisor.</span>
            </h3>
            <p className="mt-8 text-ivory/70 max-w-md font-light leading-relaxed">
              A Texas law firm advising entrepreneurs, business owners, professionals, and families
              through every stage of the business and wealth lifecycle — from formation and growth
              to financial restructuring, mergers and acquisitions, business succession planning,
              trust and estate planning, probate administration, and legacy preservation.
            </p>
            <div className="mt-6 hairline-top pt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-brass font-display text-sm md:text-base" data-testid="footer-tagline">
              <span>Build It.</span>
              <span className="text-brass/40">·</span>
              <span>Grow It.</span>
              <span className="text-brass/40">·</span>
              <span>Protect It.</span>
              <span className="text-brass/40">·</span>
              <span>Transition It.</span>
              <span className="text-brass/40">·</span>
              <span>Preserve It.</span>
            </div>
            <div className="mt-10 flex gap-4">
              <Link to="/intake" className="btn-brass" data-testid="footer-cta-intake">Request a Conference</Link>
              <Link to="/contact" className="btn-ghost !border-ivory !text-ivory hover:!bg-ivory hover:!text-navy" data-testid="footer-link-contact">Contact</Link>
            </div>

            <div className="mt-10 hairline-top pt-6 border-t-0 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-ivory/50">Principal Office · Frisco</div>
                <a href={MAIN_OFFICE_PHONE.href} className="mt-1 inline-block font-display text-2xl md:text-3xl text-ivory hover:text-brass transition" data-testid="footer-phone-main-office">
                  {MAIN_OFFICE_PHONE.display}
                </a>
              </div>
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-[0.32em] text-ivory/50">Toll-Free · Statewide</div>
                <a href={FIRM_PHONE.href} className="mt-1 inline-block font-display text-2xl text-ivory hover:text-brass transition" data-testid="footer-phone-main">
                  {FIRM_PHONE.display}
                </a>
              </div>
              <div className="pt-3">
                <div className="text-[10px] uppercase tracking-[0.32em] text-brass/80">PEAK American Financial · Clients &amp; Representatives</div>
                <a href={PEAK_PHONE.href} className="mt-1 inline-block font-cormorant italic text-2xl text-brass hover:text-ivory transition" data-testid="footer-phone-peak">
                  {PEAK_PHONE.display}
                </a>
              </div>
              <div className="pt-3">
                <div className="text-[10px] uppercase tracking-[0.32em] text-ivory/50">By Correspondence</div>
                <a href={FIRM_EMAIL.href} className="mt-1 inline-block font-display text-lg md:text-xl text-ivory hover:text-brass transition break-all" data-testid="footer-email">
                  {FIRM_EMAIL.display}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-6">
            <div className="kicker kicker-ivory">Practice</div>
            <ul className="mt-5 space-y-3 text-ivory/80 font-light">
              <li><Link to="/practice" className="hover:text-brass transition">Formation</Link></li>
              <li><Link to="/practice" className="hover:text-brass transition">Growth Capital</Link></li>
              <li><Link to="/practice" className="hover:text-brass transition">Asset Protection</Link></li>
              <li><Link to="/practice" className="hover:text-brass transition">Restructuring</Link></li>
              <li><Link to="/practice" className="hover:text-brass transition">Succession</Link></li>
              <li><Link to="/practice" className="hover:text-brass transition">Legacy & Trust</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="kicker kicker-ivory">Our Approach</div>
            <ul className="mt-5 space-y-3 text-ivory/80 font-light">
              <li><Link to="/about" className="hover:text-brass transition">Our Approach</Link></li>
              <li><Link to="/locations" className="hover:text-brass transition">Locations</Link></li>
              <li><Link to="/faq" className="hover:text-brass transition">FAQ</Link></li>
              <li><Link to="/journal" className="hover:text-brass transition">Journal</Link></li>
              <li><Link to="/contact" className="hover:text-brass transition">Contact</Link></li>
              <li><Link to="/for-advisors" className="hover:text-brass transition">For Trusted Advisors</Link></li>
              <li><Link to="/login" className="hover:text-brass transition">Client Portal</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="kicker kicker-ivory">Offices</div>
            <div className="mt-5 space-y-5 text-ivory/80 font-light text-sm leading-relaxed">
              {OFFICES.map((o) => (
                <Link
                  to={`/locations/${o.slug}`}
                  key={o.region}
                  className="block group"
                  data-testid={`footer-office-${o.slug}`}
                >
                  <div className="font-serif italic text-brass text-base flex items-baseline gap-2 group-hover:text-ivory transition">
                    {o.region}
                    {o.label === "Principal Offices" && (
                      <span className="text-[10px] tracking-[0.22em] uppercase text-ivory/40 not-italic font-sans">· Principal</span>
                    )}
                  </div>
                  <div className="text-ivory/75">{o.line1}</div>
                  <div className="text-ivory/75">{o.line2}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t-0 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-3 flex md:justify-start justify-center">
            <LSCMonogram size={140} tone="ivory" />
          </div>
          <div className="md:col-span-6 text-center md:text-left">
            <div className="font-cormorant italic text-2xl md:text-3xl text-brass/90 leading-snug">
              &ldquo;Sub umbra alarum tuarum.&rdquo;
            </div>
            <div className="mt-1 text-xs uppercase tracking-[0.32em] text-ivory/50">
              Under the shadow of thy wings
            </div>
          </div>
          <div className="md:col-span-3 text-xs text-ivory/50 tracking-[0.2em] uppercase text-center md:text-right space-y-1">
            <div>© {new Date().getFullYear()} Pratt Law Group, PLLC d/b/a Lone Star Counsel</div>
            <div>All Rights Reserved</div>
            <div className="text-ivory/35">Attorney Advertising</div>
            <div className="text-ivory/35">Past Results Do Not Guarantee Future Outcomes</div>
          </div>
        </div>

        {/* ================================================================
            RECENTLY CONSULTED — a discreet trust signal. Four anonymised
            client industries + geography, updated periodically. No names,
            no confidentiality risk; just a quiet indication that the
            counsel table is active.
        ================================================================= */}
        <div
          className="mt-16 hairline-top pt-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
          data-testid="footer-recently-consulted"
        >
          <div className="shrink-0">
            <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
              Recently Consulted
            </div>
            <div className="mt-1 font-cormorant italic text-ivory/60 text-sm leading-snug">
              a selection of active matters, anonymised.
            </div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2.5" data-testid="recently-consulted-pills">
            {[
              "Physician Group · Frisco",
              "Family Office · Houston",
              "Franchise Operator · Dallas",
              "Creator Enterprise · Austin",
            ].map((label, i) => (
              <span
                key={label}
                className="text-[10px] uppercase tracking-[0.28em] font-medium text-ivory/85 px-4 py-1.5 border border-brass/40 hover:border-brass transition-colors"
                data-testid={`recently-consulted-pill-${i}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Follow Us + Legal bottom strip */}
        <div className="mt-12 hairline-top pt-6 flex flex-col md:flex-row items-center justify-between gap-6 border-t-0" data-testid="footer-legal-bar">
          <div className="flex items-center gap-4" data-testid="footer-social">
            <span className="text-[10px] uppercase tracking-[0.32em] text-ivory/60">Follow Us</span>
            <a href="https://www.linkedin.com/company/lone-star-counsel" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="inline-flex h-9 w-9 items-center justify-center border border-transparent text-ivory/85 hover:text-brass hover:border-brass transition" data-testid="social-linkedin">
              <Linkedin size={16} strokeWidth={1.5} />
            </a>
            <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="inline-flex h-9 w-9 items-center justify-center border border-transparent text-ivory/85 hover:text-brass hover:border-brass transition" data-testid="social-facebook">
              <Facebook size={16} strokeWidth={1.5} />
            </a>
            <a href={FIRM_EMAIL.href} aria-label="Email" className="inline-flex h-9 w-9 items-center justify-center border border-transparent text-ivory/85 hover:text-brass hover:border-brass transition" data-testid="social-email">
              <Mail size={16} strokeWidth={1.5} />
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 md:gap-6 text-[10px] uppercase tracking-[0.28em] text-ivory/55">
            <Link to="/privacy" className="hover:text-brass transition" data-testid="footer-privacy">Privacy Policy</Link>
            <span className="text-ivory/30">|</span>
            <Link to="/terms" className="hover:text-brass transition" data-testid="footer-terms">Terms of Use</Link>
            <span className="text-ivory/30">|</span>
            <Link to="/legal" className="hover:text-brass transition" data-testid="footer-legal">Legal Disclosures</Link>
            <span className="text-ivory/30">|</span>
            <Link to="/site-map" className="hover:text-brass transition" data-testid="footer-sitemap">Site Map</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
